// src/features/records/components/CalendarView/MonthView/DayCell.js
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getVisibleRecords } from '../utils/calendarData';
import { Avatar, AvatarSkeleton } from '../../../../../components/avatars';
import { formatDate } from '../../../../../utils/dateUtils';
import styles from './CalendarGrid.module.css';

const formatDayNumber = (date, locale) => {
  const dayPart = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
  })
    .formatToParts(date)
    .find((part) => part.type === 'day')?.value;

  return dayPart || String(date.getDate());
};

const DayCell = memo(({ 
  date, 
  records = [], 
  isToday, 
  isCurrentMonth, 
  onDayClick,
  onRecordClick,
  userProfiles = {},
  profilesLoading = false
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const dayNumber = formatDayNumber(date, locale);
  const fullDateLabel = formatDate(date, {
    locale,
    t,
    formatOptions: { month: 'long', day: 'numeric', year: 'numeric' },
  });
  const eventsCountLabel =
    records.length > 0
      ? t('records.time.calendar.events_count', {
          count: records.length,
        })
      : '';
  const { visible: visibleRecords, overflowCount } = getVisibleRecords(records, 3, {
    locale,
    t,
  });
  
  const handleDayClick = (e) => {
    // Only trigger day click if not clicking on a record
    if (e.target === e.currentTarget || e.target.classList.contains(styles.dateNumber)) {
      onDayClick(date);
    }
  };
  
  const handleRecordClick = (e, record) => {
    e.stopPropagation();
    onRecordClick(record);
  };
  
  const cellClasses = [
    styles.calendarCell,
    !isCurrentMonth && styles.otherMonth,
    isToday && styles.today
  ].filter(Boolean).join(' ');
  
  return (
    <div 
      className={cellClasses}
      onClick={handleDayClick}
      role="gridcell"
      aria-label={`${fullDateLabel}${eventsCountLabel ? `, ${eventsCountLabel}` : ''}`}
    >
      <div className={isToday ? styles.todayNumber : styles.dateNumber}>
        {dayNumber}
      </div>
      
      {visibleRecords.length > 0 && (
        <div className={styles.recordsContainer}>
          {visibleRecords.map((record) => {
            const ownerId = record?.access?.owner ?? record?.ownerId ?? null;
            const ownerProfile =
              (ownerId && userProfiles[ownerId]) ||
              record?.ownerProfile ||
              null;
            const ownerName =
              ownerProfile?.displayName ||
              ownerProfile?.username ||
              record?.ownerName ||
              t('records.views.calendar.item.user_fallback_name');
            const ownerAvatarSrc =
              ownerProfile?.profilePictureUrl ||
              ownerProfile?.profilePicture ||
              ownerProfile?.photoURL ||
              record?.ownerAvatar ||
              null;
            const shouldShowAvatarSkeleton = Boolean(profilesLoading && ownerId && !ownerProfile);
            const avatarColorSeed = ownerId || record?.id || ownerName;

            return (
              <div
                key={record.id}
                className={styles.recordItem}
                style={{
                  '--record-color': record.color,
                  '--record-color-soft': record.tint,
                  '--record-color-soft-hover': record.tintHover
                }}
                onClick={(e) => handleRecordClick(e, record)}
                title={`${record.title}${
                  record.isAllDay
                    ? ` (${t('records.time.all_day')})`
                    : ` (${record.time})`
                }`}
              >
                {shouldShowAvatarSkeleton ? (
                  <AvatarSkeleton className={styles.recordAvatar} />
                ) : (
                  <Avatar
                    className={styles.recordAvatar}
                    name={ownerName}
                    colorSeed={avatarColorSeed}
                    src={ownerAvatarSrc || undefined}
                    alt={
                      ownerName
                        ? t('records.views.calendar.item.user_avatar_alt', { name: ownerName })
                        : t('records.views.calendar.item.user_avatar_fallback_alt')
                    }
                  />
                )}
                <span className={styles.recordTitle}>{record.title}</span>
              </div>
            );
          })}
          
          {overflowCount > 0 && (
            <div className={styles.moreRecords}>
              {t('records.time.calendar.more_count', {
                count: overflowCount,
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

DayCell.displayName = 'DayCell';

export default DayCell;
